import React, { ComponentProps } from 'react';
import { FormattedMessage } from '@kyruus/intl';
import { PMCConfig } from '@kyruus/types';
import { Box } from '@biome-design-system/ui/Box';

import { Log, MarketableLocation } from 'Src/types';
import messages from '../messages';
import { SectionHeader, ContentSection } from '../styles';
import { shouldNotRenderAboutSection } from '../utils';
import {
  Video,
  Lists,
  ListSectionHeader,
  ListSection,
  AccessibilityList,
  AccessibilityListItem,
  ListSectionHeaderWithAside
} from './styles';
import { Description } from './Description';
import { Hours } from './Hours';
import { Contacts } from './Contacts';

interface BaseAboutProps {
  marketableLocation: MarketableLocation;
  log: Log;
  titleId: string;
  showAppointmentAside?: boolean;
  config: PMCConfig;
}

type AboutProps = BaseAboutProps & ComponentProps<'section'>;

export const About = ({
  marketableLocation,
  log,
  titleId,
  showAppointmentAside,
  config,
  ...props
}: AboutProps) => {
  const {
    location_description,
    hours_of_operation,
    contacts,
    urls,
    media,
    accessibility
  } = marketableLocation;
  const videoMedia = media?.find((item) => item.media_type === 'video');

  const showContacts = (contacts?.length ?? 0) > 0;
  const showHours = (hours_of_operation?.periods?.length ?? 0) > 0;
  const showAccessibility = (accessibility?.length ?? 0) > 0;
  const showDescription = !!location_description;
  const showVideo = !!videoMedia;
  const showLists = showContacts || showHours || showAccessibility;

  if (shouldNotRenderAboutSection(marketableLocation)) {
    return null;
  }
  const { autolink } = config?.provider_v9 ?? {};
  let listContent = <></>;

  if (showLists) {
    listContent = (
      <Lists>
        {showContacts && (
          <ListSection>
            <FormattedMessage {...messages.contactsTitle}>
              {(text) => (
                <ListSectionHeader id="location-about-contacts-title">
                  {text}
                </ListSectionHeader>
              )}
            </FormattedMessage>
            <Contacts
              contacts={contacts ?? []}
              urls={urls}
              log={log}
              aria-labelledby="location-about-contacts-title"
            />
          </ListSection>
        )}

        {showHours && hours_of_operation && (
          <ListSection>
            <FormattedMessage {...messages.hoursTitle}>
              {(text) => (
                <ListSectionHeader id="location-about-hours-title">
                  {text}
                </ListSectionHeader>
              )}
            </FormattedMessage>
            <Hours
              hours={hours_of_operation}
              aria-labelledby="location-about-hours-title"
            />
          </ListSection>
        )}

        {showAccessibility && (
          <ListSection>
            <FormattedMessage {...messages.accessibilityTitle}>
              {(text) => (
                <ListSectionHeader id="location-about-accessibility-title">
                  {text}
                </ListSectionHeader>
              )}
            </FormattedMessage>
            <AccessibilityList aria-labelledby="location-about-accessibility-title">
              {accessibility?.map(({ name }) => (
                <AccessibilityListItem key={name}>{name}</AccessibilityListItem>
              ))}
            </AccessibilityList>
          </ListSection>
        )}
      </Lists>
    );

    // The order of how things are rendered is different depending on if the AppointmentAside is showing
    if (showAppointmentAside) {
      listContent = (
        <Lists>
          {showHours && hours_of_operation && (
            <ListSection>
              <FormattedMessage {...messages.hoursTitle}>
                {(text) => (
                  <ListSectionHeaderWithAside id="location-about-hours-title">
                    {text}
                  </ListSectionHeaderWithAside>
                )}
              </FormattedMessage>
              <Hours
                hours={hours_of_operation}
                aria-labelledby="location-about-hours-title"
              />
              {showContacts && (
                <Box sx={{ mt: 2 }}>
                  <FormattedMessage {...messages.contactsTitle}>
                    {(text) => (
                      <ListSectionHeaderWithAside id="location-about-contacts-title">
                        {text}
                      </ListSectionHeaderWithAside>
                    )}
                  </FormattedMessage>
                  <Contacts
                    contacts={contacts ?? []}
                    urls={urls}
                    log={log}
                    aria-labelledby="location-about-contacts-title"
                  />
                </Box>
              )}
            </ListSection>
          )}
          {showAccessibility && (
            <ListSection>
              <FormattedMessage {...messages.accessibilityTitle}>
                {(text) => (
                  <ListSectionHeaderWithAside id="location-about-accessibility-title">
                    {text}
                  </ListSectionHeaderWithAside>
                )}
              </FormattedMessage>
              <AccessibilityList aria-labelledby="location-about-accessibility-title">
                {accessibility?.map(({ name }) => (
                  <AccessibilityListItem key={name}>
                    {name}
                  </AccessibilityListItem>
                ))}
              </AccessibilityList>
            </ListSection>
          )}
        </Lists>
      );
    }
  }

  return (
    <ContentSection
      data-testid="location-about"
      aria-labelledby={titleId}
      {...props}
    >
      <FormattedMessage {...messages.aboutTitle}>
        {(text) => <SectionHeader id={titleId}>{text}</SectionHeader>}
      </FormattedMessage>

      {listContent}
      {showDescription && (
        <Description
          locationDescription={location_description}
          autolink={autolink}
          data-testid="location-about-description"
        />
      )}
      {showVideo && (
        <Video
          accessibilityText={videoMedia.accessibility_text}
          url={videoMedia.url}
          width="100%"
          height="315"
          testId="mloc-video"
        />
      )}
    </ContentSection>
  );
};
